import React, { useEffect, useState, useRef } from "react"
import "./Policy.scss"
import { useNavigate } from "react-router-dom"
import {
  Button,
  Col,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Input,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap"
import { Toast } from "primereact/toast"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"
import PolicyApi from "Api/PolicyApi"
import DataTableLoader from "SkeletonLoader/DataTableLoader"

const Policy = () => {
  document.title = "Policies"
  const navigate = useNavigate()
  const accessToken = localStorage.getItem("accessToken")
  const [policyDetails, setPolicyDetails] = useState([])
  const [filteredPolicyDetails, setFilteredPolicyDetails] = useState([])
  const [page, setPage] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const [limit, setLimit] = useState(10)
  const [loading, setLoading] = useState(true)
  const [selectedPolicies, setSelectedPolicies] = useState([])
  const [deleteModal, setDeleteModal] = useState(false)
  const [policyToDelete, setPolicyToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")

  const deleteToggle = () => setDeleteModal(!deleteModal)
  const toast = useRef(null)

  const breadcrumbItems = [
    { label: "Pixwik", link: "/" },
    { label: "Policy", link: "/Policy" },
    { label: "Policy List" },
  ]
  
  const handleBreadcrumbClick = link => {
    if (link !== "#") {
      navigate(link)
    }
  }

  const getAllPolicies = () => {
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      params: {
        limit: limit,
        page: page,
      },
    }

    PolicyApi.getPolicy(config)
      .then(response => {
        setLoading(false)
        setPolicyDetails(response.data.data || [])
        setTotalRecords(response.data.totalPolicies || response.data.data?.length || 0)
      })
      .catch(error => {
        console.error("Error fetching policies:", error)
        setLoading(false)
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to fetch policies",
          life: 3000,
        })
      })
  }

  const searchPolicies = () => {
    if (!searchQuery.trim()) {
      getAllPolicies()
      return
    }

    setLoading(true)
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }

    PolicyApi.searchPolicy(searchQuery, config)
      .then(response => {
        setLoading(false)
        setPolicyDetails(response.data.data || [])
        setFilteredPolicyDetails(response.data.data || [])
      })
      .catch(error => {
        console.error("Error searching policies:", error)
        setLoading(false)
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to search policies",
          life: 3000,
        })
      })
  }

  useEffect(() => {
    getAllPolicies()
  }, [accessToken, page, limit])

  useEffect(() => {
    setFilteredPolicyDetails(policyDetails)
  }, [policyDetails])

  const totalPages = Math.ceil(totalRecords / limit)

  const handleAddPolicyClick = () => {
    navigate("/addPolicy")
  }

  const toggleSelectAll = () => {
    if (selectedPolicies.length === filteredPolicyDetails.length) {
      setSelectedPolicies([])
    } else {
      setSelectedPolicies(filteredPolicyDetails.map(policy => policy._id))
    }
  }

  const toggleSelection = policyId => {
    setSelectedPolicies(prev =>
      prev.includes(policyId)
        ? prev.filter(id => id !== policyId)
        : [...prev, policyId],
    )
  }

  const handleDeletePolicy = id => {
    if (!id) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Invalid policy ID",
        life: 3000,
      })
      return
    }

    setPolicyToDelete(id)
    setDeleteModal(true)
  }

  const confirmDelete = () => {
    if (!policyToDelete) return

    setIsDeleting(true)
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }

    PolicyApi.deletePolicy(policyToDelete, config)
      .then(response => {
        setIsDeleting(false)
        setDeleteModal(false)
        setPolicyToDelete(null)
        
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Policy deleted successfully",
          life: 3000,
        })
        
        getAllPolicies()
      })
      .catch(error => {
        setIsDeleting(false)
        console.error("Error deleting policy:", error)
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: error.response?.data?.message || "Failed to delete policy",
          life: 3000,
        })
      })
  }

  const handleCloseModal = () => {
    setDeleteModal(false)
    setPolicyToDelete(null)
  }

  const handleSearch = (e) => {
    e.preventDefault()
    searchPolicies()
  }

  return (
    <>
      <Toast ref={toast} position="top-right" life="3000" />
      <div className="policy-container">
        <Row>
          <Col xs="12">
            <div className="policy-list-header d-flex align-items-center justify-content-between">
              <CustomBreadCrumb
                data={breadcrumbItems}
                pageTitle="Policies"
                handleClick={handleBreadcrumbClick}
              />
              <Button
                type="button"
                color="secondary"
                className="waves-effect"
                onClick={handleAddPolicyClick}
              >
                Add Policy
              </Button>
            </div>

            {/* Search Bar */}
            {/* <div className="search-container mt-3 mb-3">
              <form onSubmit={handleSearch} className="d-flex gap-2">
                <Input
                  type="text"
                  placeholder="Search policies..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-grow-1"
                />
                <Button type="submit" color="primary">
                  Search
                </Button>
                {searchQuery && (
                  <Button 
                    type="button" 
                    color="secondary"
                    onClick={() => {
                      setSearchQuery("")
                      getAllPolicies()
                    }}
                  >
                    Clear
                  </Button>
                )}
              </form>
            </div> */}

            <div className="policy-data-table table-responsive mt-4 mb-3">
              {loading ? (
                <DataTableLoader cols={6} />
              ) : (
                <Table className="table mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          onClick={toggleSelectAll}
                          checked={
                            selectedPolicies.length ===
                            filteredPolicyDetails.length
                          }
                        />
                      </th>
                      <th>#</th>
                      <th>Policy Name</th>
                      <th>Policy Description</th>
                      <th>Edit</th>
                      <th className="text-center">Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPolicyDetails.map((data, index) => (
                      <tr key={data._id} className="policy-table-row">
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedPolicies.includes(data._id)}
                            onClick={() => toggleSelection(data._id)}
                          />
                        </td>
                        <th scope="row">{(page - 1) * limit + index + 1}</th>
                        <td>{data.policyName}</td>
                        <td>
                          {data.policyDescription && data.policyDescription.length > 100
                            ? `${data.policyDescription.substring(0, 100)}...`
                            : data.policyDescription}
                        </td>
                        <td>
                          <span
                            className="edit-policy"
                            onClick={() =>
                              navigate(`/editPolicy/${data._id}`, {
                                state: data,
                              })
                            }
                            style={{ cursor: "pointer" }}
                          >
                            <i className="ion ion-md-create fs-4" />
                          </span>
                        </td>
                        <td className="text-center">
                          <span
                            className="delete-policy"
                            onClick={() => handleDeletePolicy(data._id)}
                            style={{ cursor: "pointer" }}
                          >
                            <i className="ion ion-md-trash fs-4" />
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>

            {!loading && filteredPolicyDetails.length === 0 && (
              <div className="text-center mt-4">
                <p className="text-muted">No policies found</p>
              </div>
            )}

            <div className="pagination-container d-flex align-items-center justify-content-end">
              <Pagination>
                <PaginationItem disabled={page <= 1}>
                  <PaginationLink previous onClick={() => setPage(page - 1)} />
                </PaginationItem>
                {[...Array(Math.min(3, totalPages))].map((_, index) => (
                  <PaginationItem key={index} active={index + 1 === page}>
                    <PaginationLink onClick={() => setPage(index + 1)}>
                      {index + 1}
                    </PaginationLink>
                  </PaginationItem>
                ))}

                {totalPages > 6 && page > 3 && page < totalPages - 2 && (
                  <PaginationItem disabled>
                    <PaginationLink>...</PaginationLink>
                  </PaginationItem>
                )}

                {totalPages > 6 && page > 3 && page < totalPages - 2 && (
                  <PaginationItem active>
                    <PaginationLink onClick={() => setPage(page)}>
                      {page}
                    </PaginationLink>
                  </PaginationItem>
                )}

                {totalPages > 6 && page < totalPages - 2 && (
                  <PaginationItem disabled>
                    <PaginationLink>...</PaginationLink>
                  </PaginationItem>
                )}

                {totalPages > 3 &&
                  [...Array(Math.min(3, totalPages))].map((_, index) => {
                    const pageNumber = totalPages - 2 + index
                    return (
                      <PaginationItem
                        key={pageNumber}
                        active={pageNumber === page}
                      >
                        <PaginationLink onClick={() => setPage(pageNumber)}>
                          {pageNumber}
                        </PaginationLink>
                      </PaginationItem>
                    )
                  })}
                <PaginationItem disabled={page >= totalPages}>
                  <PaginationLink next onClick={() => setPage(page + 1)} />
                </PaginationItem>
              </Pagination>
            </div>
          </Col>
        </Row>
      </div>

      <Modal isOpen={deleteModal} toggle={handleCloseModal} centered>
        <ModalHeader toggle={handleCloseModal}>Delete Policy</ModalHeader>
        <ModalBody>
          <div className="text-center">
            <i
              className="ri-delete-bin-2-line"
              style={{ fontSize: "3rem", color: "#f46a6a" }}
            />
            <h4 className="mt-3">Are you sure?</h4>
            <p className="text-muted">
              Do you really want to delete this policy? This action cannot be
              undone.
            </p>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button
            color="danger"
            onClick={confirmDelete}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" />
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default Policy